import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import Footer from '../../components/Footer';
import AppStoreBadges from '../../components/AppStoreBadges';
import FeatureIcon, { ICON_TONES } from '../../components/FeatureIcon';
import { EVENT_TYPES } from '../../components/canonicalAccents';
import { IOS_SNAPSHOT_BASE } from '../../components/MarketingScreenshot';
import { getScreenshotPreview, getScreenshotPreviewSet } from '../../lib/screenshotPreview';
import { selectedCaptureSrc } from '../../lib/selectedCaptures';
import EventHistory from './EventHistory';
import EventLifecycleOverview from './EventLifecycleOverview';

const photoHeading = '#f8fafc';
const photoBody = 'rgba(226,232,240,0.9)';

const ArrowIcon: React.FC = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
  </svg>
);

const Tick: React.FC<{ title: string; sub: string; primary: string; secondary: string; accent: string }> = ({ title, sub, primary, secondary, accent }) => (
  <li className="flex items-start gap-3">
    <span className="mt-1 text-base font-extrabold flex-shrink-0" style={{ color: accent }} aria-hidden="true">✓</span>
    <div>
      <h3 className="font-semibold text-base mb-0.5" style={{ color: primary }}>{title}</h3>
      <p className="text-sm leading-relaxed" style={{ color: secondary, textWrap: 'pretty' }}>{sub}</p>
    </div>
  </li>
);

const AnimatedSection: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1, rootMargin: '0px' });
  const reducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return (
    <div
      ref={ref}
      style={
        reducedMotion
          ? undefined
          : {
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: `opacity 1.2s ease-out ${delay}ms, transform 1s ease-out ${delay}ms`
            }
      }
    >
      {children}
    </div>
  );
};

const Events: React.FC = () => {
  const { colors, theme, dsColors } = useTheme();
  const [discoveryView, setDiscoveryView] = useState(0);

  const heroPrimary = colors.textPrimary;
  const heroSecondary = colors.textSecondary;
  const cardBorder = theme === 'light' ? 'rgba(0,0,0,0.1)' : colors.border;
  const deviceBorder = theme === 'light' ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.2)';
  const shot = (base: string) =>
    `/assets/screenshots/ios-delivery/${base} - ${theme === 'dark' ? 'Dark' : 'Light'}.png`;

  const deviceFrame = 'rounded-[2rem] border-4 overflow-hidden shadow-2xl';

  const discoveryViews = [
    { src: selectedCaptureSrc('dashboardMap', theme), label: 'Map view', cap: 'Every nearby event, on one map.' },
    { src: shot('Event Details/iOS - Event Details'), label: 'Event details', cap: 'The host, the plan, the roster.' }
  ];

  const eventTypes = [
    { id: 'cruise' as const, title: 'Cruise', description: 'Relaxed group rides at a casual pace', best: 'Scenic routes, social riding' },
    { id: 'hangout' as const, title: 'Hangout', description: 'Social focus with coffee and food stops', best: 'Meeting riders, making friends' },
    { id: 'tour' as const, title: 'Tour', description: 'Longer scenic rides and day trips', best: 'Adventure, discovering new routes' },
    { id: 'workshop' as const, title: 'Workshop', description: 'Learning and technical sessions', best: 'Skill building, education' }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* 1 · Hero — café meetup; copy on the quiet left wall, riders uncovered */}
      <section className="relative bg-[#0a0d14] md:bg-transparent">
        <div className="md:hidden aspect-[16/10] bg-cover bg-[position:62%_center]" style={{ backgroundImage: `url('/assets/images/Personal Photos/2024-10-14_group-bikes-night-mural-wall.png')` }} role="img" aria-label="A row of motorcycles parked in front of a painted mural wall at a night meet" />
        <div
          className="absolute inset-0 hidden md:block bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(100deg, rgba(8,10,16,0.72) 0%, rgba(8,10,16,0.5) 38%, rgba(8,10,16,0.14) 62%, transparent 80%), url('/assets/images/Personal Photos/2024-10-14_group-bikes-night-mural-wall.png')`
          }}
        />
        <div className="relative container mx-auto px-4 sm:px-6 max-w-6xl py-16 md:py-32 lg:py-36">
          <div className="max-w-xl text-center md:text-left mx-auto md:mx-0">
            <p className="text-xs sm:text-sm font-bold uppercase tracking-widest mb-3" style={{ color: dsColors.primary }}>
              Events
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-[1.05]" style={{ color: photoHeading, textWrap: 'balance' }}>
              Create &amp; Join<br className="hidden md:block" /> Motorcycle Events
            </h1>
            <p className="text-lg sm:text-xl leading-relaxed mb-8 max-w-md mx-auto md:mx-0" style={{ color: photoBody, textWrap: 'pretty' }}>
              See who is hosting, the kind of ride, and who is bringing what, before you commit. Or host your own.
            </p>
            <div className="flex justify-center md:justify-start">
              <AppStoreBadges size="large" showApple showAndroid />
            </div>
          </div>
        </div>
      </section>

      {/* 2 · Lifecycle journey — connected six-stage line, details in Help */}
      <AnimatedSection>
        <EventLifecycleOverview />
      </AnimatedSection>

      {/* 3 · Before you join — motorcycle-specific context leads; tabbed map + detail evidence */}
      <AnimatedSection delay={100}>
        <section className="py-16 md:py-24" style={{ backgroundColor: colors.surface }}>
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-center">
              <div className="text-center md:text-left">
                <p className="text-xs sm:text-sm font-bold uppercase tracking-widest mb-3" style={{ color: dsColors.primary }}>
                  Finding &amp; joining events
                </p>
                <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: heroPrimary, textWrap: 'balance' }}>
                  See who is riding before you commit.
                </h2>
                <p className="text-lg leading-relaxed mb-8" style={{ color: heroSecondary, textWrap: 'pretty' }}>
                  Browse motorcycle events on the map and filter by type, distance, skill level, and date. Open an event to see the host, the plan, and the roster. Share a link to bring your crew.
                </p>
                <p className="text-sm leading-relaxed mb-8 -mt-4" style={{ color: heroSecondary, textWrap: 'pretty' }}>
                  Find new people to ride with, or bring your own crew together through invite-only events.
                </p>
                <ul className="space-y-6 text-left">
                  <Tick title="A named host and a real plan" sub="Every event lists its host, the type of ride, pace and skill expectations, and any motorcycle requirements." primary={heroPrimary} secondary={heroSecondary} accent={dsColors.primary} />
                  <Tick title="Who is coming, and on what" sub="The roster shows participants and the motorcycle each one is bringing. Invite-only events reveal the host, roster, and meetup after you enter the host's code." primary={heroPrimary} secondary={heroSecondary} accent={dsColors.primary} />
                  <Tick title="The conversation stays with the event" sub="Event chat keeps coordination in one place: meetup questions, timing, and day-of updates." primary={heroPrimary} secondary={heroSecondary} accent={dsColors.primary} />
                </ul>
              </div>
              <div className="flex flex-col items-center gap-4">
                <div
                  role="tablist"
                  aria-label="Discovery views"
                  className="inline-flex gap-1 p-1 rounded-full"
                  style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}` }}
                  onKeyDown={(e) => {
                    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
                    e.preventDefault();
                    const next = discoveryView === 0 ? 1 : 0;
                    setDiscoveryView(next);
                    (e.currentTarget.querySelectorAll('[role="tab"]')[next] as HTMLElement).focus();
                  }}
                >
                  {discoveryViews.map((v, i) => (
                    <button
                      key={v.label}
                      role="tab"
                      aria-selected={discoveryView === i}
                      tabIndex={discoveryView === i ? 0 : -1}
                      onClick={() => setDiscoveryView(i)}
                      className="px-5 py-2.5 rounded-full text-sm font-semibold focus:outline-none focus-visible:ring-2"
                      style={{
                        '--tw-ring-color': dsColors.primary,
                        backgroundColor: discoveryView === i ? dsColors.primaryStrong : 'transparent',
                        color: discoveryView === i ? '#ffffff' : heroSecondary
                      } as React.CSSProperties}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
                <img
                  src={getScreenshotPreview(discoveryViews[discoveryView].src)}
                  srcSet={getScreenshotPreviewSet(discoveryViews[discoveryView].src)}
                  sizes="(min-width: 768px) 288px, (min-width: 640px) 256px, 240px"
                  alt={discoveryViews[discoveryView].label === 'Map view' ? 'RidePulse dashboard map view' : 'Event details with RSVP in RidePulse'}
                  loading="lazy"
                  width={1320}
                  height={2868}
                  className={`${deviceFrame} w-60 sm:w-64 md:w-72 max-w-full`}
                  style={{ borderColor: deviceBorder }}
                />
                <p className="text-sm text-center max-w-xs" style={{ color: heroSecondary }}>
                  {discoveryViews[discoveryView].cap}
                </p>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* 3 · Four types — single bordered strip */}
      <AnimatedSection delay={150}>
        <section className="py-16 md:py-24" style={{ backgroundColor: colors.background }}>
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: heroPrimary, textWrap: 'balance' }}>
              Four Types of Events
            </h2>
            <p className="text-lg sm:text-xl max-w-2xl mx-auto mb-10" style={{ color: heroSecondary, textWrap: 'pretty' }}>
              Each type sets the expectations for pace and purpose up front.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px rounded-2xl overflow-hidden text-left" style={{ border: `1px solid ${cardBorder}`, backgroundColor: cardBorder }}>
              {eventTypes.map((type) => (
                <div
                  key={type.title}
                  className="p-6"
                  style={{ backgroundColor: colors.surface }}
                >
                  <h3 className="text-lg font-bold mb-2 flex items-center gap-2" style={{ color: EVENT_TYPES[type.id].color }}>
                    <FeatureIcon name={EVENT_TYPES[type.id].icon} size={18} className="flex-shrink-0" />{type.title}
                  </h3>
                  <p className="text-sm mb-4" style={{ color: heroSecondary, textWrap: 'pretty' }}>{type.description}</p>
                  <p className="text-xs font-bold uppercase tracking-wider" style={{ color: heroSecondary }}>Best for: {type.best}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* 4 · Event chat — compact benefit in the participation story; CAP-14 capture */}
      <AnimatedSection delay={180}>
        <section className="py-16 md:py-24" style={{ backgroundColor: theme === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.02)' }}>
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-10 md:gap-14 items-center">
              <div className="max-w-xl text-center md:text-left mx-auto md:mx-0">
                <p className="text-xs sm:text-sm font-bold uppercase tracking-widest mb-3" style={{ color: dsColors[ICON_TONES.community] }}>
                  Event Chat
                </p>
                <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: heroPrimary, textWrap: 'balance' }}>
                  The conversation belongs with the event.
                </h2>
                <p className="text-lg leading-relaxed mb-8" style={{ color: heroSecondary, textWrap: 'pretty' }}>
                  Coordinate with the people taking part, ask questions, and keep event messages together instead of spreading the plan across separate group chats.
                </p>
                <Link
                  to="/help#host-chat"
                  className="font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded"
                  style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary } as React.CSSProperties}
                >
                  How event chat works →
                </Link>
              </div>
              <div className="flex justify-center md:justify-end">
                <figure className="w-52 sm:w-60 max-w-full flex-shrink-0 m-0">
                  <img src={shot('Event Details/iOS - Event Details - Event Chat')} alt="A pre-event chat in RidePulse with a pinned host message and replies from riders" loading="lazy" className={`${deviceFrame} w-full`} style={{ borderColor: deviceBorder }} />
                  <figcaption className="text-xs mt-3 text-center" style={{ color: heroSecondary }}>
                    Event chat.
                  </figcaption>
                </figure>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* 5 · Create — device pair + condensed host pitch; steps live in Help */}
      <AnimatedSection delay={200}>
        <section className="py-16 md:py-24" style={{ backgroundColor: colors.surface }}>
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-center">
              <div className="flex justify-center gap-3 sm:gap-4 items-end">
                <img src={shot(`${IOS_SNAPSHOT_BASE}/Events/iOS - Events - Create Event`)} alt="Create event form in RidePulse" loading="lazy" className={`${deviceFrame} w-48 sm:w-56 md:w-60 max-w-full`} style={{ borderColor: deviceBorder }} />
                <img src={shot(`${IOS_SNAPSHOT_BASE}/Events/iOS - Events - Location Selector`)} alt="Event location selector in RidePulse" loading="lazy" className={`${deviceFrame} w-28 sm:w-32 md:w-36 max-w-full translate-y-4 sm:translate-y-6`} style={{ borderColor: deviceBorder }} />
              </div>
              <div className="text-center md:text-left">
                <p className="text-xs sm:text-sm font-bold uppercase tracking-widest mb-3" style={{ color: dsColors.primary }}>
                  Create an Event
                </p>
                <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: heroPrimary, textWrap: 'balance' }}>
                  Host the ride you want to take.
                </h2>
                <p className="text-lg leading-relaxed mb-6 max-w-lg mx-auto md:mx-0" style={{ color: heroSecondary, textWrap: 'pretty' }}>
                  Set the meetup, route, pace, and requirements, including the motorcycles that fit. Choose who can join: public, friends only, or invite only. Then publish and build the group you want to ride with.
                </p>
                <p className="mb-8">
                  <Link
                    to="/help#create-event"
                    className="font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded"
                    style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary } as React.CSSProperties}
                  >
                    Step-by-step in the Help guide →
                  </Link>
                </p>
                <p
                  className="inline-block text-sm font-semibold px-4 py-2 rounded-lg"
                  style={{ backgroundColor: `${dsColors.primary}20`, color: dsColors.primary }}
                >
                  Tip: Scout your route beforehand and check road conditions
                </p>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* 5 · Event tools — deduplicated features; privacy detail lives on /privacy */}
      <AnimatedSection delay={250}>
        <section className="py-16 md:py-24" style={{ backgroundColor: colors.background }}>
          <div className="container mx-auto px-4 sm:px-6 max-w-5xl text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: heroPrimary, textWrap: 'balance' }}>
              Built for hosts and riders
            </h2>
            <p className="text-lg sm:text-xl max-w-2xl mx-auto mb-10" style={{ color: heroSecondary, textWrap: 'pretty' }}>
              Coordination before, during, and after the ride.
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 text-left">
              {[
                { icon: 'users' as const, tone: 'community' as const, title: 'Participant Roster', sub: 'See who joined and who checked in. Hosts can remove a rider when needed.' },
                { icon: 'chat' as const, tone: 'community' as const, title: 'Event Chat', sub: 'Coordinate with participants before and during the ride. Hosts can moderate and pin messages.' },
                { icon: 'checkin' as const, tone: 'safety' as const, title: 'Check-In', sub: 'Nearby check-in or host-managed check-in confirms arrival and the motorcycle you’re bringing.' },
                { icon: 'bell' as const, tone: 'attention' as const, title: 'Event Notifications', sub: 'Alerts for event changes, cancellations, and updates from the host.' },
                { icon: 'shield' as const, tone: 'safety' as const, title: 'Per-Event Privacy', sub: 'Your profile defaults stay unchanged. Optionally share a phone number or emergency contact with this event’s host.' },
                { icon: 'edit' as const, tone: 'neutral' as const, title: 'Host Controls', sub: 'Edit or cancel the event when plans change.' }
              ].map((f) => (
                <li key={f.title} className="p-5 rounded-xl" style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}` }}>
                  <span className="inline-flex w-9 h-9 rounded-lg items-center justify-center mb-3" style={{ backgroundColor: `${dsColors[ICON_TONES[f.tone]]}1f`, color: dsColors[ICON_TONES[f.tone]] }} aria-hidden="true">
                    <FeatureIcon name={f.icon} size={18} />
                  </span>
                  <h3 className="font-semibold text-base mb-1" style={{ color: heroPrimary }}>{f.title}</h3>
                  <p className="text-sm" style={{ color: heroSecondary, textWrap: 'pretty' }}>{f.sub}</p>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm max-w-2xl mx-auto" style={{ color: heroSecondary }}>
              How visibility and contact sharing work:{' '}
              <Link to="/privacy" className="font-semibold hover:underline" style={{ color: dsColors.primary }}>Privacy &amp; Visibility</Link>
              {' · '}
              <Link to="/help#blocking-reporting" className="font-semibold hover:underline" style={{ color: dsColors.primary }}>Blocking and reporting</Link>
            </p>
            <div
              className="mt-8 rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-5 text-left"
              style={{ backgroundColor: colors.surface, border: `2px solid ${dsColors.primary}` }}
            >
              <div className="flex-1 text-center sm:text-left">
                <h3 className="text-xl font-bold mb-1" style={{ color: heroPrimary }}>GroupPulse Real-Time Tracking</h3>
                <p className="text-sm" style={{ color: heroSecondary }}>Pro feature for enhanced safety and coordination</p>
              </div>
              <Link
                to="/grouppulse"
                className="font-bold inline-flex items-center gap-2 flex-shrink-0"
                style={{ color: dsColors.primary }}
              >
                Learn more <ArrowIcon />
              </Link>
            </div>
            <p
              className="mt-8 inline-block text-sm font-semibold px-5 py-3 rounded-xl"
              style={{ backgroundColor: `${dsColors.primary}10`, border: `1px solid ${dsColors.primary}30`, color: heroPrimary }}
            >
              RidePulse provides tools for coordination and communication. Always ride responsibly and within your abilities.
            </p>
          </div>
        </section>
      </AnimatedSection>

      {/* 6 · After the ride — participant archive capture in context;
             chat-history copy is held for owner review, not rendered */}
      <AnimatedSection delay={300}>
        <EventHistory />
      </AnimatedSection>

      {/* 7 · Close — badges + cross-links */}
      <section className="py-16 md:py-20" style={{ backgroundColor: colors.background }}>
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl text-center">
          <div className="mb-8 flex justify-center">
            <AppStoreBadges size="large" showApple showAndroid />
          </div>
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-3">
            <Link to="/grouppulse" className="font-semibold border-b pb-0.5" style={{ color: heroPrimary, borderColor: heroSecondary }}>GroupPulse</Link>
            <Link to="/hosting" className="font-semibold border-b pb-0.5" style={{ color: heroPrimary, borderColor: heroSecondary }}>For Hosts</Link>
            <Link to="/social" className="font-semibold border-b pb-0.5" style={{ color: heroPrimary, borderColor: heroSecondary }}>Community</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Events;
